import { createContext, useContext } from 'react';

export type Density = 'comfortable' | 'compact';
export type ShellView = 'feed' | 'tracker' | 'settings';

export interface ShellContextValue {
  addUrlOpen: boolean;
  closeAddUrl: () => void;
  closePalette: () => void;
  density: Density;
  openAddUrl: () => void;
  openPalette: () => void;
  paletteOpen: boolean;
  pendingView: ShellView | null;
  requestView: (next: ShellView) => void;
  resolvePendingView: (confirmed: boolean) => void;
  setDensity: (density: Density) => void;
  setSettingsDirty: (dirty: boolean) => void;
  settingsDirty: boolean;
  setView: (next: ShellView) => void;
  view: ShellView;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

const useShellContext = () => {
  const ctx = useContext(ShellContext);

  if (!ctx)
    throw new Error('useShellContext must be used within DashboardProvider');

  return ctx;
};

export const useShellNav = () => {
  const { view, setView, requestView } = useShellContext();

  return { requestView, setView, view };
};

export const useDisplayDensity = () => {
  const { density, setDensity } = useShellContext();

  return { density, setDensity };
};

export const useOverlays = () => {
  const {
    addUrlOpen,
    closeAddUrl,
    closePalette,
    openAddUrl,
    openPalette,
    paletteOpen,
  } = useShellContext();

  return {
    addUrlOpen,
    closeAddUrl,
    closePalette,
    openAddUrl,
    openPalette,
    paletteOpen,
  };
};

export const useSettingsNav = () => {
  const { pendingView, resolvePendingView, setSettingsDirty, settingsDirty } =
    useShellContext();

  return { pendingView, resolvePendingView, setSettingsDirty, settingsDirty };
};
